import React, { useContext, useEffect, useState } from 'react';
import { ACTION_CAP, CapId, ViewId } from './types';

export interface ConfirmOptions {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
}

export interface ToastOptions {
  kind?: 'ok' | 'err' | 'info';
}

export interface AdminCtxValue {
  caps: CapId[];
  can: (cap: CapId | null | undefined) => boolean;
  canAction: (action: string) => boolean;
  // Raw message to the client service (no cap check, no feedback)
  send: (payload: Record<string, unknown>) => void;
  // A panel action: refused locally without the cap, a pending toast until the server answers
  act: (action: string, extra?: Record<string, unknown>, pendingLabel?: string) => void;
  toast: (text: string, opts?: ToastOptions) => void;
  confirm: (opts: ConfirmOptions) => void;
  goto: (view: ViewId, opts?: { locationQuery?: string; profileId?: number }) => void;
  // Registers what Escape does while a side sheet is open (the most recent one wins); returns the cleanup
  onEscape: (handler: () => void) => () => void;
}

const noop = (): void => undefined;

export const AdminCtx = React.createContext<AdminCtxValue>({
  caps: [],
  can: () => false,
  canAction: () => false,
  send: noop,
  act: noop,
  toast: noop,
  confirm: noop,
  goto: noop,
  onEscape: () => noop,
});

export const useAdmin = (): AdminCtxValue => useContext(AdminCtx);

export const capOfAction = (action: string): CapId | null => (Object.prototype.hasOwnProperty.call(ACTION_CAP, action) ? ACTION_CAP[action] : null);

// CEF-friendly clipboard write: navigator.clipboard needs a secure
// context/permission the in-game browser may not grant, so fall back to the
// classic hidden-textarea + execCommand trick.
export const copyToClipboard = (value: string): boolean => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).catch(() => undefined);
    }
  } catch (e) { /* ignore */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
};

// "3j 4h 12m" style, dropping leading zero units
export const formatUptime = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days} j`);
  if (days || hours) parts.push(`${hours} h`);
  if (days || hours || minutes) parts.push(`${minutes} min`);
  if (!days) parts.push(`${seconds} s`);
  return parts.join(' ');
};

export const formatCountdown = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

export const formatDate = (ms: number): string => {
  if (!ms) return '-';
  try {
    return new Date(ms).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return new Date(ms).toLocaleString();
  }
};

export const formatAgo = (ms: number, now: number): string => {
  const s = Math.max(0, Math.floor((now - ms) / 1000));
  if (s < 5) return "à l'instant";
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
};

export const plural = (n: number, one: string, many: string): string => (n > 1 ? many : one);

// ---- density (comfortable / compact), remembered by the shell ----------------

export type Density = 'comfortable' | 'compact';

export const DensityCtx = React.createContext<Density>('comfortable');
export const useDensity = (): Density => useContext(DensityCtx);

// Row heights of the virtualized tables, per density
export const rowHeightFor = (density: Density, comfortable: number): number => (density === 'compact' ? Math.round(comfortable * 0.8) : comfortable);

// A value that follows its source after a pause: filters do not recompute on every keystroke
export function useDebounced<T>(value: T, ms = 120): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

// "dans 5 jours" / "dans 3 h" / "dans 12 min", for a future instant
export const formatIn = (ms: number, now: number): string => {
  const s = Math.max(0, Math.floor((ms - now) / 1000));
  const m = Math.floor(s / 60);
  if (m < 60) return `dans ${Math.max(1, m)} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `dans ${h} h`;
  const d = Math.floor(h / 24);
  return `dans ${d} jours`;
};
